import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { publicDriverBody } from '@pulsepoint/shared';
import { Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import type { z } from 'zod';
import { useOnboarding, useSubmitOnboarding } from '../../api/drivers';
import { applyApiError } from '../../components/form/apiErrors';
import { FieldRow, FormSection } from '../../components/form/FieldRow';
import { FormTextField } from '../../components/form/FormTextField';
import { AuthLayout } from '../../components/layout/AuthLayout';
import { colours, radius } from '../../theme';
import { mono } from './CardsPanel';

type In = z.input<typeof publicDriverBody>;
type Out = z.output<typeof publicDriverBody>;
const FIELDS = ['name', 'email', 'phone', 'postcode', 'vehicleType', 'registration'];

/**
 * The page a driver opens from their organisation's onboarding link. No account needed:
 * they fill in their details and cards, and their organisation approves them.
 */
export function JoinPage() {
  const { token = '' } = useParams();
  const link = useOnboarding(token);
  const send = useSubmitOnboarding(token);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const form = useForm<In, unknown, Out>({
    resolver: zodResolver(publicDriverBody),
    defaultValues: { name: '', email: '', phone: '', postcode: '', vehicleType: '', registration: '', cards: [], website: '' },
  });
  const cards = useFieldArray({ control: form.control, name: 'cards' });

  // The link is a key: keep it out of search engines and out of Referer headers.
  useEffect(() => {
    const metas = [
      ['robots', 'noindex, nofollow'],
      ['referrer', 'no-referrer'],
    ].map(([name, content]) => Object.assign(document.createElement('meta'), { name, content }));
    metas.forEach((m) => document.head.appendChild(m));
    return () => metas.forEach((m) => m.remove());
  }, []);

  if (link.isPending) {
    return (
      <AuthLayout title="Join Pulsepoint.">
        <Skeleton variant="rounded" height={240} />
      </AuthLayout>
    );
  }
  if (link.error || !link.data) {
    return (
      <AuthLayout title="This link doesn't work." lead="It has expired or been turned off. Ask your organisation for a new one.">
        <span />
      </AuthLayout>
    );
  }
  if (done) {
    return (
      <AuthLayout title="Thank you." lead={`Your details are with ${link.data.organisationName}.`}>
        <Typography>They'll check them and turn on your cards. You don't need to do anything else; you can close this page.</Typography>
      </AuthLayout>
    );
  }

  const submit = form.handleSubmit(async (v) => {
    setError('');
    try {
      await send.mutateAsync(v);
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (e) {
      setError(applyApiError(e, form.setError, [...FIELDS, ...v.cards.map((_c, i) => `cards.${i}.tag`)]));
    }
  });

  return (
    <AuthLayout title="Join Pulsepoint." lead={`For ${link.data.organisationName}'s EV charging. It takes a minute.`}>
      <Box component="form" onSubmit={submit} noValidate sx={{ display: 'grid', gap: 4 }}>
        <FormSection title="About you">
          <FormTextField control={form.control} name="name" label="Full name" autoComplete="name" autoFocus />
          <FormTextField control={form.control} name="email" label="Email" type="email" autoComplete="email" />
          <FieldRow columns="2fr 1fr">
            <FormTextField control={form.control} name="phone" label="Mobile" type="tel" autoComplete="tel" placeholder="04XX XXX XXX" />
            <FormTextField control={form.control} name="postcode" label="Postcode" autoComplete="postal-code" slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 4 } }} />
          </FieldRow>
        </FormSection>

        <FormSection title="Your car">
          <FormTextField control={form.control} name="vehicleType" label="Make and model" placeholder="e.g. Tesla Model 3" />
          <FormTextField control={form.control} name="registration" label="Registration" slotProps={{ htmlInput: { autoCapitalize: 'characters' } }} />
        </FormSection>

        <FormSection title="Charging cards">
          <Typography color="textSecondary" sx={{ mt: -1 }}>
            If you have a card or key fob for the chargers, add the number printed on it. You can skip this.
          </Typography>
          {cards.fields.map((f, i) => (
            <Box key={f.id} sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 1, alignItems: 'start', p: 2, border: `1px solid ${colours.hairline}`, borderRadius: `${radius.panel}px` }}>
              <Box sx={{ display: 'grid', gap: 2 }}>
                <FormTextField control={form.control} name={`cards.${i}.tag`} label="Card number" slotProps={{ htmlInput: { autoCapitalize: 'characters', spellCheck: false, style: mono } }} autoFocus={i === cards.fields.length - 1 && i > 0} />
                <FormTextField control={form.control} name={`cards.${i}.label`} label="Label (optional)" placeholder="e.g. Blue key fob" />
              </Box>
              <IconButton aria-label={`Remove card ${i + 1}`} onClick={() => cards.remove(i)}>
                <X size={20} strokeWidth={1.75} />
              </IconButton>
            </Box>
          ))}
          {cards.fields.length < 5 && (
            <Box>
              <Button startIcon={<Plus size={18} strokeWidth={1.75} />} onClick={() => cards.append({ tag: '', label: '' })}>
                {cards.fields.length ? 'Add another card' : 'Add a card'}
              </Button>
            </Box>
          )}
        </FormSection>

        {/* People never see this; form-filling bots fill it in. */}
        <Box aria-hidden sx={{ position: 'absolute', left: -10_000, width: '1px', height: '1px', overflow: 'hidden' }}>
          <label>
            Website
            <input {...form.register('website')} tabIndex={-1} autoComplete="off" />
          </label>
        </Box>

        {error && <Alert severity="error" role="alert">{error}</Alert>}
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          <Button type="submit" variant="contained" size="large" disabled={send.isPending}>
            {send.isPending ? 'Sending…' : 'Send my details'}
          </Button>
          <Typography variant="body2" color="textSecondary">
            Your details go only to {link.data.organisationName} and Infinity EV, to run your charging.
          </Typography>
        </Box>
      </Box>
    </AuthLayout>
  );
}
